import React, { useMemo, useState, useRef, useLayoutEffect } from 'react';
import { useParams, useNavigate, Navigate, Link } from 'react-router-dom';
import { useAuth } from '../auth/AuthContext.jsx';
import { usersForAllRoles, ROLE_LABELS } from '../auth/roles.js';
import { ARTICLES, CATEGORIES, categoryLabel, visibleArticles, matchesQuery } from '../lib/knowledge/index.js';
import RoleChips from '../components/knowledge/RoleChips.jsx';

/**
 * Knowledge — the in-app, role-aware user guide (spec: knowledge_logic.md).
 *
 * Left rail: search + TOC of the articles this user's tier can see, grouped
 * by category. Main pane: the article. Deep links (/knowledge/:articleId) to
 * articles outside the user's tier redirect back to /knowledge — hidden
 * features stay invisible rather than showing a "no access" page.
 *
 * Preview mode (Supervisor + SuperAdmin): a "Preview as" switch swaps the
 * page's effective user for a pseudo-user of any role, so reviewers can cycle
 * through every tier and check exactly what it sees — TOC, article bodies and
 * section gates included. Page-local and instant; the rest of the app is
 * untouched (unlike Assume identity).
 */
export default function Knowledge() {
  const { user } = useAuth();
  const { articleId } = useParams();
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const [tocOpen, setTocOpen] = useState(false);
  const [previewRole, setPreviewRole] = useState('');

  const canPreview = !!user?.isSuperAdmin || user?.role === 'Supervisor';
  const previewUser = canPreview && previewRole
    ? usersForAllRoles().find(u => u.role === previewRole) || null
    : null;
  // Everything below renders from the effective user — the previewed role's
  // pseudo-user, or the real signed-in user.
  const effectiveUser = previewUser || user;

  const visible = useMemo(() => visibleArticles(effectiveUser), [effectiveUser]);

  // Independent scroll panes: fix the rail+article row to the space below the
  // page-head so each side scrolls on its own (a long TOC never drags the article,
  // and vice-versa). Recompute when the article/preview changes the header height,
  // and on resize; below 1100px the row stacks and flows normally.
  const rowRef = useRef(null);
  useLayoutEffect(() => {
    const el = rowRef.current;
    if (!el) return undefined;
    const fit = () => {
      const doc = document.documentElement;
      if (doc.clientWidth <= 1100) { el.style.height = ''; return; }
      const top = el.getBoundingClientRect().top;
      // clientHeight excludes any horizontal scrollbar, so the article bottom
      // (the pager) is never left clipped behind it. 20px = main's padding-bottom.
      el.style.height = `${Math.max(360, Math.floor(doc.clientHeight - top - 20))}px`;
    };
    fit();
    window.addEventListener('resize', fit);
    return () => window.removeEventListener('resize', fit);
  }, [articleId, previewRole]);

  // Resolve the current article; bare /knowledge opens the first visible one.
  if (!visible.length) return <Navigate to="/dashboard" replace />;
  const article = articleId ? visible.find(a => a.id === articleId) : visible[0];
  if (!article) return <Navigate to="/knowledge" replace />;

  const idx = visible.indexOf(article);
  const prev = idx > 0 ? visible[idx - 1] : null;
  const next = idx < visible.length - 1 ? visible[idx + 1] : null;

  const filtered = visible.filter(a => matchesQuery(a, query));
  // A search that matches nothing suggests the full list instead of dead-ending.
  const suggesting = query.trim().length > 0 && filtered.length === 0;
  const list = suggesting ? visible : filtered;
  const Body = article.Body;

  return (
    <>
      <header className="page-head reveal">
        <div>
          <div className="breadcrumb">
            <Link to="/knowledge">Knowledge</Link>
            <span>›</span>
            <span>{categoryLabel(article.category)}</span>
          </div>
          <h1>{article.title}</h1>
          <div className="page-sub">{article.summary}</div>
        </div>
      </header>

      {previewUser && (
        <div className="kb-preview-banner" role="status">
          <span>
            Previewing the guide as <strong>{ROLE_LABELS[previewUser.role] || previewUser.role}</strong> —
            they see {visible.length} of {ARTICLES.length} articles, exactly as listed on the left.
          </span>
          <button type="button" className="btn sm" onClick={() => setPreviewRole('')}>
            Back to my view
          </button>
        </div>
      )}

      {/* Mobile-only TOC toggle — the rail is hidden under 820px */}
      <button
        type="button"
        className="btn kb-toc-toggle"
        onClick={() => setTocOpen(o => !o)}
        aria-expanded={tocOpen}
      >
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ width: 14, height: 14 }}>
          <line x1="3" y1="6" x2="21" y2="6" /><line x1="3" y1="12" x2="21" y2="12" /><line x1="3" y1="18" x2="21" y2="18" />
        </svg>
        Browse articles
      </button>

      <div className="row cases-layout kb-layout" ref={rowRef}>
        {/* div, not <aside> — global.css styles bare aside as the app sidebar */}
        <div className={`kb-rail${tocOpen ? ' open' : ''}`}>
          {/* Preview switch + search stay pinned while the article list scrolls. */}
          <div className="kb-rail-top">
            {canPreview && (
              <label className="kb-preview-control">
                <span>Preview as</span>
                <select
                  value={previewRole}
                  onChange={e => setPreviewRole(e.target.value)}
                  aria-label="Preview the guide as another role"
                >
                  <option value="">My view</option>
                  {usersForAllRoles().map(u => (
                    <option key={u.role} value={u.role}>{ROLE_LABELS[u.role] || u.role}</option>
                  ))}
                </select>
              </label>
            )}

            <div className="search">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="11" cy="11" r="8" />
                <line x1="21" y1="21" x2="16.65" y2="16.65" />
              </svg>
              <input
                placeholder="Search articles…"
                value={query}
                onChange={e => setQuery(e.target.value)}
                aria-label="Search articles"
              />
              {query && (
                <button type="button" className="kb-search-clear" aria-label="Clear search"
                  onClick={() => setQuery('')}>×</button>
              )}
            </div>
          </div>

          <nav aria-label="Knowledge articles">
            {/* No exact match → never a dead end: fall back to every article the
                user can see, under a "Suggested" note, so there's always somewhere
                to go. */}
            {suggesting && (
              <div className="kb-toc-suggest">No exact match for “{query}”. Suggested:</div>
            )}
            {CATEGORIES.map(cat => {
              const items = list.filter(a => a.category === cat.id);
              if (!items.length) return null;
              return (
                <div key={cat.id} className="kb-toc-group">
                  <div className="kb-toc-label">{cat.label}</div>
                  {items.map(a => (
                    <button
                      key={a.id}
                      type="button"
                      className={`kb-toc-item${a.id === article.id ? ' active' : ''}`}
                      onClick={() => { setTocOpen(false); navigate(`/knowledge/${a.id}`); }}
                    >
                      {a.title}
                    </button>
                  ))}
                </div>
              );
            })}
          </nav>
        </div>

        <div className="kb-article" key={article.id + (previewUser ? `@${previewUser.role}` : '')}>
          <div className="kb-article-meta">
            <RoleChips visibleTo={article.visibleTo} />
          </div>
          <Body user={effectiveUser} />

          <div className="kb-pager reveal">
            {prev ? (
              <button type="button" className="btn" onClick={() => navigate(`/knowledge/${prev.id}`)}>
                ← {prev.title}
              </button>
            ) : <span />}
            {next ? (
              <button type="button" className="btn" onClick={() => navigate(`/knowledge/${next.id}`)}>
                {next.title} →
              </button>
            ) : <span />}
          </div>
        </div>
      </div>
    </>
  );
}
